import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const ZONES = ['SE1', 'SE2', 'SE3', 'SE4'];

export default function AIForecastDashboard() {
    const [zone, setZone] = useState('SE3');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchForecast = async (z) => {
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const res = await axios.get(`/api/ai-forecast?zone=${z}`, { timeout: 60000 });
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Kunde inte generera AI-prognos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchForecast(zone); }, [zone]);

    const buildChartData = () => {
        if (!data?.items?.length) return null;
        const labels = data.items.map((item, i) => {
            const d = new Date(item.ts);
            return isNaN(d) ? `H${i + 1}` : `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:00`;
        });

        return {
            labels,
            datasets: [
                {
                    label: 'P90 (Optimistisk)',
                    data: data.items.map(i => i.p90),
                    borderColor: '#60a5fa40',
                    backgroundColor: '#3b82f610',
                    fill: '+1',
                    pointRadius: 0,
                    tension: 0.4,
                    borderWidth: 1,
                    borderDash: [4, 2],
                },
                {
                    label: 'P50 (Prognos)',
                    data: data.items.map(i => i.p50),
                    borderColor: '#3b82f6',
                    backgroundColor: '#3b82f615',
                    fill: false,
                    pointRadius: 0,
                    tension: 0.4,
                    borderWidth: 2,
                },
                {
                    label: 'P10 (Pessimistisk)',
                    data: data.items.map(i => i.p10),
                    borderColor: '#60a5fa40',
                    backgroundColor: '#3b82f610',
                    fill: '-1',
                    pointRadius: 0,
                    tension: 0.4,
                    borderWidth: 1,
                    borderDash: [4, 2],
                },
            ]
        };
    };

    const getStats = () => {
        if (!data?.items?.length) return null;
        const p50s = data.items.map(i => i.p50).filter(Boolean);
        return {
            min: Math.min(...p50s).toFixed(1),
            max: Math.max(...p50s).toFixed(1),
            avg: (p50s.reduce((a, b) => a + b, 0) / p50s.length).toFixed(1),
            hours: data.horizon_hours,
        };
    };

    const chartData = buildChartData();
    const stats = getStats();

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white p-6">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-start gap-3 mb-2">
                    <h1 className="text-3xl font-bold">AI-prisprognos</h1>
                    <span className="mt-1.5 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded-full">
                        Experimentell
                    </span>
                </div>
                <p className="text-gray-400 mb-6">
                    48-timmarsprognos för elpriser med SMHI-väderdata &amp; OpenAI · P10/P50/P90-scenarion
                </p>

                {/* Zone Selector */}
                <div className="flex gap-2 mb-6">
                    {ZONES.map(z => (
                        <button
                            key={z}
                            onClick={() => setZone(z)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                zone === z ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                        >
                            {z}
                        </button>
                    ))}
                </div>

                {loading && (
                    <div className="text-center py-20 text-gray-400">
                        <div className="animate-spin h-10 w-10 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                        <p className="text-lg">Genererar AI-prognos för {zone}...</p>
                        <p className="text-sm mt-1">Hämtar väderdata, prisunderlag &amp; kör modellen</p>
                    </div>
                )}

                {error && (
                    <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 text-red-300">
                        <p className="font-semibold mb-1">Prognos otillgänglig</p>
                        <p className="text-sm">{error}</p>
                        <p className="text-sm mt-2 text-red-400/70">
                            Se till att <code className="bg-black/30 px-1 rounded">OPENAI_API_KEY</code> är angiven för AI-förbättrade prognoser.
                            Utan den returneras en baslinjeprogons.
                        </p>
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                            {[
                                { label: 'Medelpris', value: `${stats?.avg} EUR/MWh` },
                                { label: 'Min (P50)', value: `${stats?.min} EUR/MWh` },
                                { label: 'Max (P50)', value: `${stats?.max} EUR/MWh` },
                                { label: 'Horisont', value: `${stats?.hours}h` },
                            ].map(s => (
                                <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <p className="text-xs text-gray-400 mb-1">{s.label}</p>
                                    <p className="text-xl font-bold">{s.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* Chart */}
                        {chartData && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                                <h2 className="text-lg font-semibold mb-1">48-timmarsprognos — {zone}</h2>
                                <p className="text-xs text-gray-500 mb-4">Skuggat område = P10–P90 osäkerhetsintervall</p>
                                <div className="h-72">
                                    <Line
                                        data={chartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { labels: { color: '#9ca3af', boxWidth: 12 } },
                                                tooltip: {
                                                    mode: 'index',
                                                    callbacks: {
                                                        label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1)} EUR/MWh`
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    ticks: {
                                                        color: '#6b7280',
                                                        maxTicksLimit: 12,
                                                        maxRotation: 45,
                                                    },
                                                    grid: { color: 'rgba(255,255,255,0.05)' }
                                                },
                                                y: {
                                                    ticks: {
                                                        color: '#6b7280',
                                                        callback: v => `${v} €`
                                                    },
                                                    grid: { color: 'rgba(255,255,255,0.05)' }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* AI Comment */}
                        {data.comment && (
                            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-5">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 w-6 h-6 rounded-full bg-blue-500/30 flex items-center justify-center shrink-0">
                                        <svg className="w-3.5 h-3.5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-blue-400 mb-1">Prognosdrivare</p>
                                        <p className="text-sm text-gray-300 leading-relaxed">{data.comment}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <p className="text-xs text-gray-600 mt-4">
                            Genererad: {data.generated_at ? new Date(data.generated_at).toLocaleString('sv-SE') : '—'} ·
                            Plats: {data.location || zone}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
